import React from 'react';
import { Alert, Button, Box, Typography, Collapse } from '@mui/material';
import { Warning as WarningIcon, Payment as PaymentIcon } from '@mui/icons-material';
import { usePayment } from '../../contexts/PaymentContext';

/**
 * Banner de aviso de pagamento em atraso
 * Exibido no topo da pagina quando ha pendencias financeiras
 */
const PaymentBanner = () => {
  const {
    status,
    statusData,
    loading,
    isEditBlocked,
    openPaymentModal,
    STATUS
  } = usePayment();

  // Nao exibir se ainda carregando ou se nao ha problema
  if (loading || !status || status === STATUS.ATIVO) {
    return null;
  }

  // Trial - mostrar banner informativo
  if (status === STATUS.TRIAL) {
    return (
      <Collapse in>
        <Alert
          severity="info"
          sx={{
            mb: 2,
            borderRadius: 2,
            '& .MuiAlert-message': { width: '100%' }
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
            <Box>
              <Typography variant="subtitle2" fontWeight="bold">
                Periodo de teste
              </Typography>
              <Typography variant="body2">
                {statusData?.mensagem || 'Voce esta no periodo de avaliacao gratuita.'}
              </Typography>
            </Box>
          </Box>
        </Alert>
      </Collapse>
    );
  }

  // Atrasado (ate 5 dias) - aviso amarelo
  if (status === STATUS.ATRASADO && !isEditBlocked()) {
    return (
      <Collapse in>
        <Alert
          severity="warning"
          icon={<WarningIcon />}
          sx={{
            mb: 2,
            borderRadius: 2,
            '& .MuiAlert-message': { width: '100%' }
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
            <Box>
              <Typography variant="subtitle2" fontWeight="bold">
                Pagamento em atraso
              </Typography>
              <Typography variant="body2">
                {statusData?.mensagem || `Sua mensalidade esta ${statusData?.diasAtraso || 0} dias em atraso.`}
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="warning"
              size="small"
              startIcon={<PaymentIcon />}
              onClick={() => openPaymentModal()}
              sx={{ ml: 2, whiteSpace: 'nowrap' }}
            >
              Pagar agora
            </Button>
          </Box>
        </Alert>
      </Collapse>
    );
  }

  // Atrasado (5-15 dias) - aviso laranja/vermelho, modo somente leitura
  if (status === STATUS.ATRASADO && isEditBlocked()) {
    return (
      <Collapse in>
        <Alert
          severity="error"
          icon={<WarningIcon />}
          sx={{
            mb: 2,
            borderRadius: 2,
            backgroundColor: '#ff5722',
            color: 'white',
            '& .MuiAlert-icon': { color: 'white' },
            '& .MuiAlert-message': { width: '100%' }
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
            <Box>
              <Typography variant="subtitle2" fontWeight="bold">
                Sistema em modo somente leitura
              </Typography>
              <Typography variant="body2">
                {statusData?.mensagem || 'Pagamento em atraso. Regularize para voltar a editar.'}
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="small"
              startIcon={<PaymentIcon />}
              onClick={() => openPaymentModal()}
              sx={{
                ml: 2,
                whiteSpace: 'nowrap',
                backgroundColor: 'white',
                color: '#ff5722',
                '&:hover': {
                  backgroundColor: '#f5f5f5'
                }
              }}
            >
              Regularizar agora
            </Button>
          </Box>
        </Alert>
      </Collapse>
    );
  }

  // Bloqueio parcial (15-30 dias) ou Sem assinatura
  if (status === STATUS.BLOQUEADO_PARCIAL || status === STATUS.SEM_ASSINATURA) {
    return (
      <Collapse in>
        <Alert
          severity="error"
          icon={<WarningIcon />}
          sx={{
            mb: 2,
            borderRadius: 2,
            backgroundColor: '#d32f2f',
            color: 'white',
            '& .MuiAlert-icon': { color: 'white' },
            '& .MuiAlert-message': { width: '100%' }
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
            <Box>
              <Typography variant="subtitle2" fontWeight="bold">
                {status === STATUS.SEM_ASSINATURA ? 'Sem plano ativo' : 'Acesso limitado'}
              </Typography>
              <Typography variant="body2">
                {statusData?.mensagem || 'Acesso limitado a Inicio, Configuracoes e Suporte. Regularize o pagamento.'}
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="small"
              startIcon={<PaymentIcon />}
              onClick={() => openPaymentModal()}
              sx={{
                ml: 2,
                whiteSpace: 'nowrap',
                backgroundColor: 'white',
                color: '#d32f2f',
                '&:hover': {
                  backgroundColor: '#f5f5f5'
                }
              }}
            >
              Regularizar
            </Button>
          </Box>
        </Alert>
      </Collapse>
    );
  }

  // Bloqueio total (30+ dias) - nao deveria chegar aqui pois usuario nao pode logar
  if (status === STATUS.BLOQUEADO) {
    return (
      <Collapse in>
        <Alert
          severity="error"
          icon={<WarningIcon />}
          sx={{
            mb: 2,
            borderRadius: 2,
            backgroundColor: '#b71c1c',
            color: 'white',
            '& .MuiAlert-icon': { color: 'white' },
            '& .MuiAlert-message': { width: '100%' }
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
            <Box>
              <Typography variant="subtitle2" fontWeight="bold">
                Acesso bloqueado
              </Typography>
              <Typography variant="body2">
                {statusData?.mensagem || 'Seu acesso foi bloqueado por falta de pagamento. Entre em contato.'}
              </Typography>
            </Box>
          </Box>
        </Alert>
      </Collapse>
    );
  }

  return null;
};

export default PaymentBanner;
